import { useMemo, useState } from 'react';

import { Center, Flex, Input, InputGroup, InputRightElement, Spinner } from '@chakra-ui/react';

import { PaginationState } from '@tanstack/react-table';

import {
  DropdownOption,
  FilterData,
  Filters,
  MultiSelectionData,
  SearchNormalOutline,
} from '@aslanpay/portal-common-lib';

import { RECONCILIATION_PAGE_SIZE_OPTIONS } from 'app/components/Reconciliations/ReconciliationPagedTable';
import ResolveFindingDialog from 'app/components/Reconciliations/ResolveFindingDialog';
import EmptyState from 'app/components/UI/EmptyState';
import { Table } from 'app/components/UI/Table/Table';

import {
  SalaryDiscrepancyFixabilityValues,
  SalaryReportRowStatusValues,
} from 'app/constants/salaryReconciliations';
import { defaultPageSize } from 'app/constants/table';

import { SalaryReconciliationReportRow } from 'app/types/salaryReconciliations';

import { formatDiscrepancyReason } from 'app/utils/salaryReconciliations';
import getSalaryReconciliationReportColumns from 'app/utils/table/salaryReconciliationReport';

import type { Filter } from '@aslanpay/portal-common-lib';

const REASON_FILTER = 'Reason';
const STATUS_FILTER = 'Status';
const FIXABILITY_FILTER = 'Adjustment fixes it';
const UNKNOWN = 'UNKNOWN';

interface SalaryReconciliationProblemsTabProps {
  rows: SalaryReconciliationReportRow[];
  isRunning: boolean;
  hasReport: boolean;
  isLoadingReport: boolean;
  showCompanyColumn: boolean;
}

/**
 * @description The distinct values of one field across the report rows, as filter options; a missing value is offered
 * as "Not explained".
 */
const toOptions = (
  rows: SalaryReconciliationReportRow[],
  valueOf: (row: SalaryReconciliationReportRow) => string,
  textOf: (value: string) => string
): DropdownOption[] =>
  [...new Set(rows.map(valueOf))]
    .map((value) => ({ value, text: value === UNKNOWN ? 'Not explained' : textOf(value) }))
    .sort((a, b) => a.text.localeCompare(b.text));

/**
 * Shows a salary accrual reconciliation run's saved report: every problem open when it finished, filtered client-side
 * by reason, status and whether an adjustment would fix it. Each problem can be marked as fixed by hand.
 */
function SalaryReconciliationProblemsTab({
  rows,
  isRunning,
  hasReport,
  isLoadingReport,
  showCompanyColumn,
}: SalaryReconciliationProblemsTabProps) {
  const [{ pageIndex, pageSize }, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [reasonFilter, setReasonFilter] = useState<string[]>([]);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [fixabilityFilter, setFixabilityFilter] = useState<string[]>([]);
  const [activeFilter, setActiveFilter] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [discrepancyToResolve, setDiscrepancyToResolve] = useState<string | undefined>();
  const [fixedDiscrepancyIds, setFixedDiscrepancyIds] = useState<Set<string>>(new Set());

  const reasonOf = (row: SalaryReconciliationReportRow) => row.reason || UNKNOWN;
  const fixabilityOf = (row: SalaryReconciliationReportRow) => row.fixability || UNKNOWN;

  const filters: Filter[] = [
    {
      name: REASON_FILTER,
      type: 'category',
      data: toOptions(rows, reasonOf, (value) =>
        formatDiscrepancyReason(value as SalaryReconciliationReportRow['reason'])
      ),
      selectedValues: reasonFilter,
    },
    {
      name: STATUS_FILTER,
      type: 'category',
      data: toOptions(
        rows,
        (row) => row.status,
        (value) =>
          SalaryReportRowStatusValues[value as keyof typeof SalaryReportRowStatusValues]?.value ??
          value
      ),
      selectedValues: statusFilter,
    },
    {
      name: FIXABILITY_FILTER,
      type: 'category',
      data: toOptions(
        rows,
        fixabilityOf,
        (value) =>
          SalaryDiscrepancyFixabilityValues[value as keyof typeof SalaryDiscrepancyFixabilityValues]
            ?.value ?? value
      ),
      selectedValues: fixabilityFilter,
    },
  ];

  const filteredRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter(
      (row) =>
        (reasonFilter.length === 0 || reasonFilter.includes(reasonOf(row))) &&
        (statusFilter.length === 0 || statusFilter.includes(row.status)) &&
        (fixabilityFilter.length === 0 || fixabilityFilter.includes(fixabilityOf(row))) &&
        (!query ||
          [row.individualName, row.externalPayrollId, row.companyName]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(query)))
    );
  }, [fixabilityFilter, reasonFilter, rows, search, statusFilter]);
  const visibleRows = filteredRows.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

  const handleFilterChange = (name: string, values: (string | number)[]) => {
    if (name === REASON_FILTER) {
      setReasonFilter(values as string[]);
    } else if (name === FIXABILITY_FILTER) {
      setFixabilityFilter(values as string[]);
    } else {
      setStatusFilter(values as string[]);
    }
    setPagination({ pageIndex: 0, pageSize });
  };

  const formatFilterValue = (_: string, filterValue: string | number, filterData: FilterData) =>
    (filterData as MultiSelectionData).find((option) => option.value === filterValue)?.text ||
    String(filterValue);

  if (isRunning) {
    return (
      <EmptyState text="This reconciliation is still running. Its report will appear here when it finishes." />
    );
  }

  if (!hasReport) {
    return <EmptyState text="No report for this run" />;
  }

  if (isLoadingReport) {
    return (
      <Center w="full" py="40px" data-testid="salary-reconciliation-report-loading">
        <Spinner color="brand.purple" size="xl" />
      </Center>
    );
  }

  if (rows.length === 0) {
    return <EmptyState text="This run found no problems" />;
  }

  return (
    <Flex flexDir="column" gap="20px" w="full">
      <Flex gap="20px" align="center" wrap="wrap">
        <Filters
          activeFilter={activeFilter}
          displayFormatter={formatFilterValue}
          filters={filters}
          onFilterClose={() => setActiveFilter(undefined)}
          onFilterChange={handleFilterChange}
          onFilterClick={setActiveFilter}
        />
        <InputGroup maxW="344px" color="brand.darkGray">
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPagination({ pageIndex: 0, pageSize });
            }}
            type="text"
            placeholder="Search by person or payroll ID"
            data-testid="salary-reconciliation-report-search"
          />
          <InputRightElement w="56px" h="full">
            <SearchNormalOutline boxSize="24px" />
          </InputRightElement>
        </InputGroup>
      </Flex>

      {filteredRows.length === 0 ? (
        <EmptyState text="No problems match these filters" />
      ) : (
        <Table
          columns={getSalaryReconciliationReportColumns({
            showCompanyColumn,
            onResolve: (row) => setDiscrepancyToResolve(row.discrepancyId),
            fixedDiscrepancyIds,
          })}
          data={visibleRows}
          total={filteredRows.length}
          pageNumber={pageIndex}
          pageSize={pageSize}
          pageSizeOptions={RECONCILIATION_PAGE_SIZE_OPTIONS}
          onPaginationChange={setPagination}
          getRowId={(row) => row.discrepancyId}
        />
      )}
      <ResolveFindingDialog
        kind="salary"
        findingId={discrepancyToResolve}
        onClose={() => setDiscrepancyToResolve(undefined)}
        onResolved={(discrepancyId) =>
          setFixedDiscrepancyIds((ids) => new Set(ids).add(discrepancyId))
        }
      />
    </Flex>
  );
}

export default SalaryReconciliationProblemsTab;
